/**
 * The listings overview card: loading / error / empty states, the table and
 * the client-side pager.
 *
 * Filtering and paging both happen here over the in-memory list — the admin
 * list endpoint returns every row of the organization uncapped, so there is no
 * server search and no server paging to add, and nothing to debounce.
 *
 * Both status actions still go out as the same `PATCH /listings/{id}/status`
 * with the same single-key bodies the inline controls sent; only where the
 * user clicks changed. Publish state and availability semantics are untouched
 * (discovery risk R1 is deliberately not addressed here).
 */
import React, { useMemo } from "react";

import { usePagination } from "../../../hooks/usePagination";
import ListingRow from "./ListingRow";
import ListingsPagination from "./ListingsPagination";
import { describeListingObject } from "./utils/listingHelpers";
import { listingMatchesQuery } from "./utils/listingFormatting";

const TH = "px-4 py-2.5 text-xs font-medium text-muted-foreground";

const CARD = "overflow-hidden rounded-xl border border-border bg-card";

function TableSkeleton() {
  return (
    <div className="divide-y divide-border" data-testid="listings-skeleton">
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className="flex items-center gap-3 px-4 py-3">
          <div className="h-11 w-16 shrink-0 animate-pulse rounded-md bg-muted" />
          <div className="h-3 w-1/4 animate-pulse rounded bg-muted" />
          <div className="ml-auto h-3 w-1/5 animate-pulse rounded bg-muted" />
        </div>
      ))}
    </div>
  );
}

function CardMessage({ title, hint }) {
  return (
    <div className="px-4 py-12 text-center">
      <p className="m-0 text-sm font-semibold text-foreground">{title}</p>
      {hint ? <p className="mt-1 text-[13px] text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

export default function ListingsTable({
  listings,
  units,
  cities,
  loading,
  error,
  query,
  editingListingId,
  statusUpdateError,
  statusUpdateLoadingId,
  onPatchStatus,
  onEdit,
}) {
  const rows = useMemo(() => (Array.isArray(listings) ? listings : []), [listings]);

  const filtered = useMemo(
    () =>
      rows.filter((l) =>
        listingMatchesQuery(l, query, {
          cities,
          objectLabel: (() => {
            const d = describeListingObject(l, units);
            return `${d.shortId} · ${d.roomLabel}`;
          })(),
        })
      ),
    [rows, query, cities, units]
  );

  // usePagination clamps its page into range whenever the list shrinks, so a
  // search that narrows the result never leaves the pager past the last page.
  const { page, pageCount, pageItems, next, prev } = usePagination(filtered, {
    pageSize: 5,
  });

  const total = filtered.length;
  const from = total === 0 ? 0 : page * 5 + 1;
  const to = page * 5 + pageItems.length;

  return (
    <section aria-label="Listings" className={CARD}>
      {statusUpdateError ? (
        <p className="m-0 border-b border-border bg-rose-50 px-4 py-2 text-[13px] text-rose-800 dark:bg-rose-500/10 dark:text-rose-300">
          {statusUpdateError}
        </p>
      ) : null}

      {loading ? (
        <TableSkeleton />
      ) : error ? (
        // The hook's message is already the user-facing German sentence, so it
        // is shown as-is rather than wrapped in a second generic headline.
        <CardMessage title={error} hint="Lade die Seite neu, um es erneut zu versuchen." />
      ) : rows.length === 0 ? (
        <CardMessage
          title="Noch keine Listings"
          hint="Wähle oben eine Unit und lege dein erstes Inserat an."
        />
      ) : total === 0 ? (
        <CardMessage title="Keine Listings gefunden" hint="Passe deine Suche an." />
      ) : (
        <>
          {/* Horizontal scroll rather than a second mobile tree: the full
              mobile layout is L7's job, this only keeps the card usable. */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-border text-left">
                  <th scope="col" className={`${TH} w-[23%]`}>Objekt</th>
                  <th scope="col" className={`${TH} w-[29%]`}>Titel</th>
                  <th scope="col" className={`${TH} w-[10%]`}>Stadt</th>
                  <th scope="col" className={`${TH} w-[10%] whitespace-nowrap`}>Preis (CHF)</th>
                  <th scope="col" className={`${TH} w-[9%]`}>Status</th>
                  <th scope="col" className={`${TH} w-[11%]`}>Verfügbarkeit</th>
                  <th scope="col" className={`${TH} w-[8%] text-right`}>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((listing) => (
                  <ListingRow
                    key={listing.id}
                    listing={listing}
                    units={units}
                    cities={cities}
                    isSelected={editingListingId === listing.id}
                    isUpdating={statusUpdateLoadingId === listing.id}
                    onEdit={onEdit}
                    onPatchStatus={onPatchStatus}
                  />
                ))}
              </tbody>
            </table>
          </div>

          <ListingsPagination
            page={page}
            pageCount={pageCount}
            total={total}
            from={from}
            to={to}
            onPrev={prev}
            onNext={next}
          />
        </>
      )}
    </section>
  );
}
